(ns practitest.frontend.forms3.run.steps
  (:require
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.attachments3 :as attachments]
    [practitest.frontend.components.card-view :as card-view]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.markdown :as markdown]
    [practitest.frontend.components.overflow :as overflow]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.confirmation-modal :as confirmation-modal]
    [practitest.frontend.components2.run-status :as run-status]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.components2.tooltip :as tooltip]
    [practitest.frontend.forms3.entity-form.collapsible-section :refer [collapsible-section]]
    [practitest.frontend.forms3.run.attachments :refer [run-attachment-section]]
    [practitest.frontend.forms3.run.events :as run-events]
    [practitest.frontend.forms3.run.new-issue-dropdown :as new-issue-dropdown]
    [practitest.frontend.forms3.run.subs :as run-subs]
    [practitest.frontend.issue.bugtracker :as bugtracker]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def ^:private step-status-options
  [{:value "PASSED"  :label (get-translation "run-status.passed" "Passed")}
   {:value "FAILED"  :label (get-translation "step-status.fail" "Fail")}
   {:value "BLOCKED" :label (get-translation "step-status.block" "Block")}
   {:value "N/A"     :label (get-translation "run-status.na" "N/A")}
   {:value "NO RUN"  :label (get-translation "run-status.no-run" "No Run")}])

(defn- preconditions-row [preconditions expanded? on-toggle]
  (when (seq preconditions)
    [collapsible-section
     {:override-collapsed? (not expanded?)
      :class               "run-step-row"
      :title               [:span.run-step-row-title
                            (get-translation "test.step.preconditions-label" "Preconditions")]
      :on-toggle           (fn [_] (on-toggle))}
     [:div.run-step-row-body
      [:div.run-step-field-value preconditions]]]))

(defn- step-status-buttons [step-id run-id current-status edit-permissions? edit-locked?]
  (let [disabled?    (or (not edit-permissions?) edit-locked?)
        tooltip-text (cond
                       edit-locked?
                       (get-translation "run.edit-locked" "Locked due to test workflow")
                       (not edit-permissions?)
                       (get-translation "run.no-edit-permission" "You don't have permission to edit this run"))]
    [:div.run-step-status-buttons
     (doall
       (for [{:keys [value label]} step-status-options]
         (let [active? (= current-status value)
               color   (run-status/get-status-color value)
               btn     [:button.run-step-status-btn
                        {:class    (when active? "run-step-status-btn--active")
                         :style    (cond-> {:color color}
                                     active? (assoc :background-color color
                                                    :color "var(--pt-color-white)"))
                         :disabled disabled?
                         :on-click (fn [e]
                                     (.stopPropagation e)
                                     (when (and (not disabled?) (not active?))
                                       (mixpanel/track "Run Form Actions" {"Action" (str "Step Status Set " label)})
                                       (re-frame/dispatch [::run-events/update-step-status step-id run-id value])
                                       (dom/scroll-to-next-row! (some-> e .-target (.closest ".run-step-row")))))}
                        label]]
           ^{:key value}
           (if disabled?
             [tooltip/tooltip {:placement "top"}
              (fn [props] [:span props btn])
              tooltip-text]
             btn))))]))

(defn- step-actions-menu [{:keys [id run-id expanded? on-toggle edit-permissions? edit-locked?
                                   name description expected-results]}]
  (reagent/with-let [show-dropdown?       (reagent/atom false)
                     show-confirmation?   (reagent/atom false)
                     confirmation-details (reagent/atom nil)]
    (let [hide!               #(reset! show-dropdown? false)
          show-confirmation!  (fn [details]
                                (reset! confirmation-details details)
                                (reset! show-confirmation? true))
          hide-confirmation!  (fn [] (reset! show-confirmation? false))
          menu-item-callbacks {:hide!              hide!
                               :show-confirmation! show-confirmation!
                               :hide-confirmation! hide-confirmation!}]
      [:<>
       [card-view/confirmation-dialog show-confirmation? confirmation-details]
       [card-view/dropdown-menu
        {:show?      show-dropdown?
         :toggle!    (fn [_e]
                       (when-not @show-dropdown?
                         (mixpanel/track "Run Form Actions" {"Action" "Step Menu Opened"}))
                       (swap! show-dropdown? not))
         :hide!      hide!
         :flip?       true
         :container  new-issue-dropdown/dropdown-container-ref
         :disabled?  (or (not edit-permissions?) edit-locked?)
         :tooltip    (cond
                       edit-locked?
                       (get-translation "run.edit-locked" "Locked due to test workflow")
                       (not edit-permissions?)
                       (get-translation "run.no-edit-permission" "You don't have permission to edit this run"))
         :class      "run-step-row-menu"
         :menu-items [[card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.edit-step" "Edit Step")
                        :icon     icons/mux-pencil
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Edit Opened"})
                                    (when-not expanded? (on-toggle))
                                    (re-frame/dispatch [::run-events/start-step-edit id
                                                        {:name             (or name "")
                                                         :description      (or description "")
                                                         :expected-results (or expected-results "")}]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.add-step-above" "Add Step above")
                        :icon     icons/mux-add-step
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Added Above"})
                                    (re-frame/dispatch [::run-events/add-step id run-id true]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.add-step-below" "Add Step below")
                        :icon     icons/mux-add-step
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Added Below"})
                                    (re-frame/dispatch [::run-events/add-step id run-id false]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label        (get-translation "run.steps.delete-step" "Delete Step")
                        :icon         icons/mux-trash
                        :on-click     (fn []
                                        (mixpanel/track "Run Form Actions" {"Action" "Step Deleted"})
                                        (re-frame/dispatch [::run-events/delete-step id run-id]))
                        :confirmation {:title     (get-translation "run.steps.delete-step" "Delete Step")
                                       :body      (get-translation "run.steps.delete-step-confirm" "Are you sure you want to delete this step?")
                                       :yes-label (get-translation "generic.button.delete" "Delete")
                                       :no-label  (get-translation "generic.button.cancel" "Cancel")}}]]}]])))

(defn- step-field [{:keys [label value draft-value on-draft-change read-only? on-load-file]}]
  (let [load-file (when on-load-file
                    (fn [file]
                      (on-load-file file
                        (fn [attachment]
                          (when (and attachment on-draft-change)
                            (let [link (attachments/generate-attachment-url
                                         {:url  (:url attachment)
                                          :name (:name attachment)
                                          :type (:file_type attachment)})]
                              (on-draft-change (str draft-value "\n" link "\n"))))))))]
    [:div {:class (str "run-step-field" (when read-only? " run-step-field--readonly"))}
     [:span.run-step-field-label label]
     [markdown/markdown-editor
      (cond-> (if read-only?
                {:value value :read-only? true}
                {:value draft-value :on-change on-draft-change})
        load-file (assoc :on-load-file  load-file
                         :on-drag-over  dom/prevent-default
                         :on-drop       (fn [e]
                                          (dom/prevent-default e)
                                          (doseq [file (array-seq (.. e -dataTransfer -files)) :when file]
                                            (load-file file)))))]]))

(defn- original-test-attachment-section [step-run-id]
  (let [design-attachments @(re-frame/subscribe [::attachments/attachments :step-design step-run-id])
        notifications      (re-frame/subscribe [::attachments/notifications :step-design step-run-id])
        options-atom       (atom {:permissions {:can-upload?    false
                                                :can-preview?   true
                                                :can-download?  true
                                                :can-rename?    false
                                                :can-delete?    false
                                                :can-copy-link? true}})]
    (when (or (seq design-attachments)
              (pos? (get @notifications :uploading 0))
              (seq (get @notifications :errors)))
      [:div.run-step-original-attachments
       [:div.run-step-attachment-header
        [icons/mux-paperclip]
        [:span.run-step-field-label
         (get-translation "run.steps.original-test-attachments" "Original Test Attachments")]]
       [attachments/attachments-view
        {:options                 options-atom
         :attachments             (re-frame/subscribe [::attachments/attachments :step-design step-run-id])
         :notifications           notifications
         :entity-type             :step-design
         :show-search?            false
         :install-paste-listener? false
         :actions                 {:on-clear-notifications #(re-frame/dispatch [::attachments/clear-notifications :step-design step-run-id])}}]])))

(defn- linked-issue-row [edit-permissions? edit-locked? {:keys [id issue]}]
  [:div.linked-issue
   [:a.linked-issue-link
    {:href (router/get-href :practitest.frontend.issues/edit
                            {:project-id @(re-frame/subscribe [::subs/current-project-id])
                             :issue-id   (:id issue)}
                            nil
                            nil)}
    (str
      (get-translation "issue.singular" "Issue")
      " "
      (:display-id issue)
      " - "
      (:title issue))]
   [:span.linked-issue-status (str " ("(:status-name issue)")")]
   [buttons/icon-button
    {:icon      [icons/mux-trash]
     :disabled? (or (not edit-permissions?) edit-locked?)
     :tooltip   (cond
                  (not (or (not edit-permissions?) edit-locked?))
                  (get-translation "run.steps.unlink-issue" "Unlink issue")
                  edit-locked?
                  (get-translation "run.edit-locked" "Locked due to test workflow")
                  :else
                  (get-translation "run.no-edit-permission" "You don't have permission to edit this run"))
     :on-click  #(re-frame/dispatch [::run-events/show-unlink-confirm-modal id])}]])

(defn- step-body [{:keys [editing? draft on-draft-change draft-actual
                           description expected-results actual-results
                           edit-permissions? edit-locked? id step-design-id cancel! save! saving? linked-issues run-id]}]
  (let [read-only?    (not editing?)
        upload-file   (when (and edit-permissions? step-design-id)
                        (fn [file on-success]
                          (re-frame/dispatch [::attachments/upload file (cond-> {:entity-type     :step-design
                                                                                 :entity-id       id
                                                                                 :attachable-type "StepDesign"
                                                                                 :attachable-id   step-design-id}
                                                                           on-success (assoc :on-upload-success on-success))])))
        desc-field  [step-field {:label           (get-translation "step.description" "Description")
                                 :value           description
                                 :draft-value     (:description draft)
                                 :on-draft-change (fn [v] (on-draft-change :description v))
                                 :read-only?      read-only?
                                 :on-load-file    upload-file}]
        exp-field   [step-field {:label           (get-translation "step.expected-results" "Expected Results")
                                 :value           expected-results
                                 :draft-value     (:expected-results draft)
                                 :on-draft-change (fn [v] (on-draft-change :expected-results v))
                                 :read-only?      read-only?
                                 :on-load-file    upload-file}]
        act-field   [:div.run-step-field
                     [:span.run-step-field-label (get-translation "step.actual-results" "Actual Results")]
                     [markdown/markdown-editor
                      {:value       (or @draft-actual "")
                       :read-only?  (not edit-permissions?)
                       :min-height  70
                       :on-change   (fn [v] (reset! draft-actual v))
                       :on-blur     (fn [_]
                                      (mixpanel/track "Run Form Actions" {"Action" "Actual Results Typed"})
                                      (when (and edit-permissions?
                                                 (not= @draft-actual (or actual-results "")))
                                        (re-frame/dispatch [::run-events/update-step-actual-results id @draft-actual])))
                       :on-load-file  (when edit-permissions?
                                        (fn [file]
                                          (re-frame/dispatch [::attachments/upload file {:entity-type     :step-run
                                                                                         :entity-id       id
                                                                                         :attachable-type "StepRun"}])))
                       :on-drag-over  (when edit-permissions? dom/prevent-default)
                       :on-drop       (when edit-permissions?
                                        (fn [e]
                                          (dom/prevent-default e)
                                          (doseq [file (array-seq (.. e -dataTransfer -files)) :when file]
                                            (re-frame/dispatch [::attachments/upload file {:entity-type     :step-run
                                                                                           :entity-id       id
                                                                                           :attachable-type "StepRun"}]))))}]
                     (when (seq linked-issues)
                       [:div.run-step-linked-issues-section
                        [:div.linked-issues-section-header
                         [icons/mux-issues]
                         [:div.run-step-field-label
                          (get-translation "run.steps.linked-issues" "Linked Issues")]]
                        [:div.linked-issues
                         (doall
                           (for [linked-issue linked-issues]
                             ^{:key (:id linked-issue)}
                             [linked-issue-row edit-permissions? edit-locked? linked-issue]))]])
                     [run-attachment-section {:entity-type       :step-run
                                              :entity-id         id
                                              :attachable-type   "StepRun"
                                              :edit-permissions? edit-permissions?
                                              :edit-locked?      edit-locked?}]]
        left-panel  [:div.run-step-left
                     [:div.run-step-fields-row
                      desc-field
                      exp-field]
                     (when editing?
                       [:div.run-step-edit-actions
                        [buttons/button {:text      (get-translation "generic.button.cancel" "Cancel")
                                         :disabled? saving?
                                         :on-click  cancel!}]
                        [buttons/button {:primary?  true
                                         :disabled? saving?
                                         :icon      (when saving? [spinners/mux-spinner {:size :small}])
                                         :text      (get-translation "generic.button.save" "Save")
                                         :on-click  save!}]])
                     [original-test-attachment-section id]]
        right-panel [:div.run-step-right act-field]]
    (if editing?
      [:div.run-step-row-body--editing
       [:div.run-step-row-body left-panel right-panel]]
      [:div.run-step-row-body left-panel right-panel])))

(defn- unlink-issue-confirm-modal [step-runs]
  (let [target-id    @(re-frame/subscribe [::run-subs/unlink-issue-confirm-target-id])
        unlinking?   @(re-frame/subscribe [::subs/progress? ::run-events/unlink-issue])
        target-issue (some (fn [step]
                              (some #(when (= (:id %) target-id) (:issue %))
                                    (get-in step [:attributes :linked-issues])))
                            step-runs)]
    [confirmation-modal/confirmation-modal
     {:show?                    (some? target-id)
      :modal-class              "unlink-issue-confirm-modal"
      :on-cancel                #(re-frame/dispatch [::run-events/hide-unlink-confirm-modal])
      :on-confirm               #(re-frame/dispatch [::run-events/unlink-issue target-id])
      :show-icon?               false
      :title                    (str (get-translation "run.steps.unlink-issue" "Unlink issue") "?")
      :cancel-button-text       (get-translation "generic.button.cancel" "Cancel")
      :confirmation-button-text (get-translation "entity.traceability.unlink" "Unlink")
      :cancel-disabled?         (or unlinking? (not target-issue))
      :confirm-disabled?        (or unlinking? (not target-issue))
      :confirm-end-icon         (when unlinking? [spinners/mux-spinner {:size :small :inside? true}])}
     [:p (format-translation
           (get-translation "run.steps.unlink-issue-modal.body"
                            "Are you sure you want to remove the link to Issue ID %{id}?")
           {:id (:display-id target-issue)})]]))

(defn- step-row [{:keys [id attributes]} expanded? on-toggle run-id edit-permissions? edit-locked?]
  (reagent/with-let [draft-actual   (reagent/atom nil)]
    (let [{:keys [position name description expected-results actual-results status call-to-test-id linked-issues step-design-id]} attributes
          _               (when (nil? @draft-actual)
                            (reset! draft-actual (or actual-results "")))
          pending-edit-id @(re-frame/subscribe [::run-subs/pending-edit-step-id])
          saving?         @(re-frame/subscribe [::run-subs/step-saving? id])
          being-edited?   @(re-frame/subscribe [::run-subs/step-being-edited? id])
          draft           @(re-frame/subscribe [::run-subs/step-draft id])
          ; auto-enter edit mode when this step was just created via add-step
          _               (when (and (= id pending-edit-id) (not being-edited?))
                            (when-not expanded? (on-toggle))
                            (re-frame/dispatch [::run-events/clear-pending-edit-step])
                            (re-frame/dispatch [::run-events/start-step-edit id
                                                {:name             (or name "")
                                                 :description      (or description "")
                                                 :expected-results (or expected-results "")}]))
          on-draft-change (fn [field value]
                            (re-frame/dispatch [::run-events/update-step-draft id field value]))
          cancel!         (fn []
                            (re-frame/dispatch [::run-events/clear-step-editing id]))
          save!           (fn []
                            (re-frame/dispatch [::run-events/update-step
                                               id
                                               {:name             (:name draft)
                                                :description      (:description draft)
                                                :expected_results (:expected-results draft)}]))]
      [collapsible-section
       {:id                  (str "step-run-" id)
        :override-collapsed? (not expanded?)
        :class               "run-step-row"
        :style               {"--step-status-color" (run-status/get-status-color status)}
        :title               [:<>
                              (when call-to-test-id
                                [tooltip/tooltip
                                 {:placement "top"}
                                 (fn [hover-props]
                                   [:span.run-step-called-icon hover-props
                                    [icons/mux-folders]])
                                 (get-translation "run.steps.called-test" "Called Test")])
                              [:span.run-step-row-position (str "Step " position " - ")]
                              (if being-edited?
                                [:input.run-step-name-input
                                 {:value     (:name draft)
                                  :on-change #(on-draft-change :name (.. % -target -value))
                                  :on-click  #(.stopPropagation %)
                                  :auto-focus true}]
                                [overflow/overflow-with-mux-tooltip
                                 {}
                                 (fn [props]
                                   [:span.run-step-row-title props name])
                                 name])]
        :header-actions      [:<>
                              [step-status-buttons id run-id status edit-permissions? edit-locked?]
                              [new-issue-dropdown/new-issue-button id run-id edit-permissions? edit-locked?]
                              [step-actions-menu
                               {:id                id
                                :run-id            run-id
                                :expanded?         expanded?
                                :on-toggle         on-toggle
                                :edit-permissions? edit-permissions?
                                :edit-locked?      edit-locked?
                                :name              name
                                :description       description
                                :expected-results  expected-results}]]
        :on-toggle           (fn [collapsed?]
                               (mixpanel/track "Run Form Actions" {"Action" (if collapsed? "Step Collapsed" "Step Expanded")})
                               (on-toggle))}
       [step-body {:editing?          being-edited?
                   :draft             draft
                   :on-draft-change   on-draft-change
                   :draft-actual      draft-actual
                   :description       description
                   :expected-results  expected-results
                   :actual-results    actual-results
                   :edit-permissions? edit-permissions?
                   :edit-locked?      edit-locked?
                   :id                id
                   :step-design-id    step-design-id
                   :cancel!           cancel!
                   :save!             save!
                   :saving?           saving?
                   :linked-issues     linked-issues
                   :run-id            run-id}]])
    (finally
      (when @(re-frame/subscribe [::run-subs/step-being-edited? id])
        (re-frame/dispatch [::run-events/clear-step-editing id])))))

(defn run-steps-section [form-props]
  ;; has-hash? holds the URL hash (e.g. "#12345") when returning from "Fail and Report Internal Issue", nil otherwise
  (reagent/with-let [has-hash?                (not-empty (.-hash js/window.location))
                     _                        (when has-hash?
                                                (grid-storage/set-section-collapsed (:entity-type form-props) "run-steps-section" false))
                     section-starts-expanded? (or has-hash?
                                                  (not (grid-storage/get-section-collapsed
                                                         (:entity-type form-props)
                                                         "run-steps-section"
                                                         true)))
                     expanded-steps            (reagent/atom #{})
                     initialized?              (reagent/atom false)
                     hash-scrolled?            (reagent/atom false)]
    (let [{:keys [entity-type base-entity edit-permissions? edit-locked?]} form-props
          run-id         (:id base-entity)
          loading?       @(re-frame/subscribe [::run-subs/steps-loading?])
          preconditions  @(re-frame/subscribe [::run-subs/preconditions])
          step-runs      @(re-frame/subscribe [::run-subs/step-runs])
          target-step-id @(re-frame/subscribe [::run-subs/link-issues-target-step-id])
          target-step    (some #(when (= (:id %) target-step-id) %) step-runs)
          all-ids        (cond-> (set (map :id step-runs))
                           (seq preconditions) (conj :precondition))]
      (when (and section-starts-expanded? (not loading?) (not @initialized?) (seq all-ids))
        (reset! initialized? true)
        (reset! expanded-steps all-ids))
      (when (and (not loading?) (not @hash-scrolled?) (seq step-runs))
        (reset! hash-scrolled? true)
        (when has-hash?
          (let [step-id-str (subs has-hash? 1)]
            (reagent/after-render
              (fn []
                (when-let [el (js/document.getElementById (str "step-run-" step-id-str))]
                  (.scrollIntoView el #js {:behavior "smooth" :block "center"})))))))
      [:<>
       [:div.run-steps-overlay-root
        {:ref #(reset! new-issue-dropdown/dropdown-container-ref %)}]
       [collapsible-section
       {:entity-type    entity-type
        :storage-key    "run-steps-section"
        :default-collapsed? true
        :hide-content?  false
        :class          "run-details-panel"
        :title          (get-translation "run.steps.title" "Run Steps")
        :on-toggle      (fn [collapsed?]
                          (mixpanel/track "Run Form Actions"
                            {"Action" (str "Run Steps" (if collapsed? " Collapsed" " Expanded"))})
                          (reset! expanded-steps (if collapsed? #{} all-ids)))
        :header-actions [:div.run-steps-header-actions
                         [:span.run-pass-all-btn
                          [buttons/button
                           {:primary?  true
                            :disabled? (or (not edit-permissions?) edit-locked?)
                            :tooltip   (cond
                                         edit-locked?
                                         (get-translation "run.edit-locked" "Locked due to test workflow")
                                         (not edit-permissions?)
                                         (get-translation "run.no-edit-permission" "You don't have permission to edit this run"))
                            :text      (get-translation "run.steps.pass-all" "Pass All")
                            :on-click  (fn [_]
                                         (mixpanel/track "Run Form Actions" {"Action" "Pass All Clicked"})
                                         (re-frame/dispatch [::run-events/pass-all run-id]))}]]]}
       (if (and loading? (empty? step-runs))
         ;; Only show the full-list spinner on the initial load -
         ;; fetch-steps also runs after saving/adding/deleting any single step, and
         ;; swapping the whole list out for a spinner then would unmount every
         ;; step-row, discarding any other row's in-progress local edit state.
         [:div.run-steps-loading [spinners/mux-spinner {:size :small}]]
         [:<>
          [bugtracker/bugtracker-integration-modal]
          [new-issue-dropdown/link-issues-dialog target-step]
          [unlink-issue-confirm-modal step-runs]
          [new-issue-dropdown/link-existing-issue-modal target-step
           @(re-frame/subscribe [::run-subs/link-issue-by-id-modal-open?])
           #(re-frame/dispatch [::run-events/hide-link-issue-by-id-modal])]
          [preconditions-row preconditions
           (contains? @expanded-steps :precondition)
           (fn [] (swap! expanded-steps
                         (fn [s] (if (contains? s :precondition)
                                   (disj s :precondition)
                                   (conj s :precondition)))))]
          [:div.run-steps-list
           (doall
             (for [step step-runs]
               (let [step-id (:id step)]
                 ^{:key step-id}
                 [step-row step
                  (contains? @expanded-steps step-id)
                  (fn [] (swap! expanded-steps
                                (fn [s] (if (contains? s step-id)
                                          (disj s step-id)
                                          (conj s step-id)))))
                  run-id
                  edit-permissions?
                  edit-locked?])))]])]])))
